/**
 * گذاشتنِ رمزِ تازه، بعد از کلیک روی لینکِ «رمزم را فراموش کردم».
 *
 * ═══ باگی که این صفحه بستش ═══
 *
 * ایمیلِ بازیابی از قبل فرستاده می‌شد و لینکش هم کار می‌کرد — کاربر را
 * **وارد** می‌کرد. ولی هیچ‌جای اپ رمز را عوض نمی‌کرد؛ در کلِ مخزن حتی یک
 * `updateUser({ password })` نبود. یعنی طرف روی صفحه‌ی اول می‌افتاد،
 * می‌دید وارد شده، و هیچ‌وقت رمزی نمی‌گذاشت. دفعه‌ی بعد که نشستش تمام
 * می‌شد، دوباره همان‌جا گیر می‌کرد.
 *
 * ═══ چرا لینک «وارد می‌کند» ═══
 *
 * لینکِ بازیابیِ Supabase یک نشستِ واقعی می‌سازد. یعنی برای عوض کردنِ رمز
 * لازم نیست چیزی از URL بخوانیم — کافی است نشست باشد. برای همین این صفحه
 * توکن را دست نمی‌زند و فقط از `useSession` می‌پرسد.
 *
 * و به همین دلیل، همین صفحه برای «عوض کردنِ رمز» در حالتِ عادی هم کار
 * می‌کند: هر کسی که وارد است می‌تواند بازش کند.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/core/lib/supabase';
import { passwordProblem } from '@/core/lib/password';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { useSession } from '@/core/session';
import { Btn, Card, Note, PasswordInput } from '@/ui/kit';

export function ResetPasswordPage() {
  useLocale();
  const { session, loading } = useSession();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // همان قاعده‌ی فرمِ ثبت‌نام (core/lib/password): کوتاه نه، همه‌حرف یا
    // بی‌حرف هم نه — وگرنه «رمزم را فراموش کردم» راهِ دورزدنِ قاعده می‌شد.
    const pw = passwordProblem(password);
    if (pw) { setError(t(pw === 'short' ? 'authPasswordShort' : 'authPasswordSimple')); return; }
    if (password !== again) { setError(t('authPasswordMismatch')); return; }

    setBusy(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (err) {
      reportError('web.resetPassword', err);
      setError(err.message);
      return;
    }
    setDone(true);
  };

  if (loading) return <div className="feed"><p className="dim">…</p></div>;

  /**
   * بدونِ نشست کاری از این صفحه برنمی‌آید.
   *
   * شایع‌ترین دلیلش این است که لینکِ ایمیل منقضی شده — و آن‌وقت پیامِ
   * «وارد شو» گیج‌کننده است، چون طرف دقیقاً برای همین آمده که نمی‌تواند
   * وارد شود. پس راهِ درست را نشان می‌دهیم: دوباره درخواست بده.
   */
  if (!session) {
    return (
      <div className="feed">
        <Card>
          <h1>{t('authSetPassword')}</h1>
          <Note>{t('authResetNeedsLink')}</Note>
          <p style={{ marginTop: 10 }}><Link to="/auth">{t('authGoSignIn')}</Link></p>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="feed">
        <Card>
          <h1>{t('authSetPassword')}</h1>
          <Note tone="brand">{t('authPasswordChanged')}</Note>
          <Btn label={t('home')} onClick={() => nav('/', { replace: true })} />
        </Card>
      </div>
    );
  }

  return (
    <div className="feed">
      <Card>
        <h1>{t('authSetPassword')}</h1>
        <form onSubmit={submit}>
          <PasswordInput autoComplete="new-password"
            placeholder={t('authNewPassword')} value={password} onChange={setPassword} />
          <PasswordInput autoComplete="new-password"
            placeholder={t('authNewPasswordAgain')} value={again} onChange={setAgain} />
          {error ? <p className="error">{error}</p> : null}
          <button type="submit" className="btn fill block" disabled={busy || !password}>
            {busy ? '…' : t('authSetPassword')}
          </button>
        </form>
      </Card>
    </div>
  );
}
